/* ==========================================================================
   BUCHHOLZ INTRODUCTIONS
   Instant search, type filters, and a photo lightbox for the cultivar grid.

   The 212 tiles are already in the DOM (server-rendered), so this file only
   enhances them. The search index is built by reading the tiles rather than
   shipping a duplicate copy of the dataset, and the heavier per-cultivar detail
   (full fact tables plus 1,434 gallery photo URLs) is fetched once, lazily, the
   first time somebody actually opens a lightbox.
   ========================================================================== */
(function () {
  'use strict';

  var DETAILS_URL = (document.currentScript || {}).dataset
    ? document.currentScript.dataset.biDetails
    : '';

  var MAX_SUGGESTIONS = 8;

  /**
   * Fold text to a comparable form: lowercase, accents stripped, punctuation
   * flattened to spaces. Lets "seuss" match "Seuss's Son" and "aya saya" match
   * regardless of how the visitor types the apostrophes or accents.
   */
  function normalize(value) {
    return (value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9]+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function tokenize(value) {
    var text = normalize(value);
    return text ? text.split(' ') : [];
  }

  /** Every query token must prefix-match some word in the haystack. */
  function matches(words, tokens) {
    return tokens.every(function (token) {
      return words.some(function (word) {
        return word.indexOf(token) === 0;
      });
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Bold the matched leading run of the first query token, for the dropdown. */
  function highlight(label, tokens) {
    var safe = escapeHtml(label);
    if (!tokens.length) return safe;
    var token = tokens[0];
    var haystack = normalize(label);
    var at = haystack.indexOf(token);
    if (at === -1) return safe;
    // normalize() can change length, so locate the run in the original string.
    var pattern = new RegExp('(' + token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'i');
    return safe.replace(pattern, '<mark>$1</mark>');
  }

  function init(root) {
    var grid = root.querySelector('[data-bi-grid]');
    if (!grid) return;

    var tiles = Array.prototype.slice.call(grid.querySelectorAll('.bi-tile'));
    if (!tiles.length) return;

    var controls = root.querySelector('[data-bi-controls]');
    var input = root.querySelector('[data-bi-search]');
    var clearBtn = root.querySelector('[data-bi-clear]');
    var suggestionList = root.querySelector('[data-bi-suggestions]');
    var chipWrap = root.querySelector('[data-bi-chips]');
    var status = root.querySelector('[data-bi-status]');
    var empty = root.querySelector('[data-bi-empty]');

    // ---- index -----------------------------------------------------------
    var entries = tiles.map(function (tile) {
      var name = (tile.querySelector('.bi-tile__name') || {}).textContent || '';
      var common = (tile.querySelector('.bi-tile__common') || {}).textContent || '';
      var category = tile.dataset.biCat || '';
      return {
        tile: tile,
        id: tile.dataset.biId,
        name: name.trim(),
        common: common.trim(),
        category: category,
        words: normalize(name + ' ' + common + ' ' + category).split(' ')
      };
    });

    var state = { query: '', category: 'all', visible: entries.length };

    // ---- filter ----------------------------------------------------------
    function apply() {
      var tokens = tokenize(state.query);
      var shown = 0;

      entries.forEach(function (entry) {
        var ok =
          (state.category === 'all' || entry.category === state.category) &&
          (!tokens.length || matches(entry.words, tokens));
        entry.tile.hidden = !ok;
        if (ok) shown += 1;
      });

      state.visible = shown;
      if (empty) empty.hidden = shown !== 0;

      if (status) {
        if (!tokens.length && state.category === 'all') {
          status.textContent = entries.length + ' introductions';
        } else if (shown === 0) {
          status.textContent = 'No matches';
        } else {
          status.textContent =
            'Showing ' + shown + ' of ' + entries.length +
            (state.category !== 'all' ? ' \u00b7 ' + state.category : '');
        }
      }

      if (clearBtn) clearBtn.hidden = !state.query;
    }

    // ---- chips -----------------------------------------------------------
    if (chipWrap) {
      var order = ['Maples', 'Conifers', 'Other'];
      var counts = {};
      entries.forEach(function (entry) {
        counts[entry.category] = (counts[entry.category] || 0) + 1;
      });

      var categories = ['all'].concat(
        order.filter(function (name) {
          return counts[name];
        })
      );

      categories.forEach(function (name) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bi__chip' + (name === 'all' ? ' is-active' : '');
        chip.dataset.biFilter = name;
        chip.setAttribute('aria-pressed', name === 'all' ? 'true' : 'false');
        chip.innerHTML =
          escapeHtml(name === 'all' ? 'All' : name) +
          '<span>' + (name === 'all' ? entries.length : counts[name]) + '</span>';
        chipWrap.appendChild(chip);
      });

      chipWrap.addEventListener('click', function (event) {
        var chip = event.target.closest('.bi__chip');
        if (!chip) return;
        state.category = chip.dataset.biFilter;
        Array.prototype.forEach.call(chipWrap.children, function (node) {
          var active = node === chip;
          node.classList.toggle('is-active', active);
          node.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        apply();
      });
    }

    // ---- autocomplete ----------------------------------------------------
    var activeSuggestion = -1;

    function closeSuggestions() {
      if (!suggestionList) return;
      suggestionList.hidden = true;
      suggestionList.innerHTML = '';
      activeSuggestion = -1;
      if (input) input.setAttribute('aria-expanded', 'false');
    }

    function renderSuggestions() {
      if (!suggestionList || !input) return;
      var tokens = tokenize(state.query);
      if (!tokens.length) return closeSuggestions();

      var hits = entries
        .filter(function (entry) {
          return (
            (state.category === 'all' || entry.category === state.category) &&
            matches(entry.words, tokens)
          );
        })
        .slice(0, MAX_SUGGESTIONS);

      // Nothing useful to add when the grid is already showing just that plant.
      if (!hits.length || (hits.length === 1 && state.visible === 1)) {
        return closeSuggestions();
      }

      suggestionList.innerHTML = hits
        .map(function (entry, index) {
          return (
            '<li class="bi__suggestion" role="option" id="bi-sg-' + index + '"' +
            ' aria-selected="false" data-bi-value="' + escapeHtml(entry.name) + '">' +
            '<span class="bi__suggestion-name">' + highlight(entry.name, tokens) + '</span>' +
            (entry.common
              ? '<span class="bi__suggestion-common">' + escapeHtml(entry.common) + '</span>'
              : '') +
            '</li>'
          );
        })
        .join('');

      suggestionList.hidden = false;
      activeSuggestion = -1;
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
    }

    function moveSuggestion(step) {
      if (!suggestionList || suggestionList.hidden) return;
      var options = suggestionList.querySelectorAll('.bi__suggestion');
      if (!options.length) return;

      if (activeSuggestion >= 0) {
        options[activeSuggestion].setAttribute('aria-selected', 'false');
      }
      activeSuggestion =
        (activeSuggestion + step + options.length) % options.length;

      var chosen = options[activeSuggestion];
      chosen.setAttribute('aria-selected', 'true');
      chosen.scrollIntoView({ block: 'nearest' });
      input.setAttribute('aria-activedescendant', chosen.id);
    }

    function chooseSuggestion(value) {
      state.query = value;
      if (input) input.value = value;
      closeSuggestions();
      apply();
    }

    if (input) {
      input.addEventListener('input', function () {
        state.query = input.value;
        apply();
        renderSuggestions();
      });

      input.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          moveSuggestion(1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          moveSuggestion(-1);
        } else if (event.key === 'Enter') {
          if (activeSuggestion >= 0 && suggestionList && !suggestionList.hidden) {
            event.preventDefault();
            var picked = suggestionList.querySelectorAll('.bi__suggestion')[activeSuggestion];
            if (picked) chooseSuggestion(picked.dataset.biValue);
          } else {
            closeSuggestions();
          }
        } else if (event.key === 'Escape') {
          if (suggestionList && !suggestionList.hidden) {
            event.stopPropagation();
            closeSuggestions();
          } else if (input.value) {
            input.value = '';
            state.query = '';
            apply();
          }
        }
      });

      input.addEventListener('blur', function () {
        // Delay so a click on a suggestion still registers.
        window.setTimeout(closeSuggestions, 140);
      });
    }

    if (suggestionList) {
      suggestionList.addEventListener('mousedown', function (event) {
        var option = event.target.closest('.bi__suggestion');
        if (!option) return;
        event.preventDefault();
        chooseSuggestion(option.dataset.biValue);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        state.query = '';
        if (input) {
          input.value = '';
          input.focus();
        }
        closeSuggestions();
        apply();
      });
    }

    if (empty) {
      empty.addEventListener('click', function (event) {
        var example = event.target.closest('[data-bi-example]');
        if (!example) return;
        chooseSuggestion(example.dataset.biExample);
        if (input) input.focus();
      });
    }

    // ---- lightbox --------------------------------------------------------
    var lightbox = root.querySelector('[data-bi-lightbox]');
    var details = null;
    var detailsPromise = null;
    var gallery = [];
    var position = 0;
    var lastFocused = null;

    var ui = lightbox
      ? {
          image: lightbox.querySelector('[data-bi-image]'),
          spinner: lightbox.querySelector('[data-bi-spinner]'),
          prev: lightbox.querySelector('[data-bi-prev]'),
          next: lightbox.querySelector('[data-bi-next]'),
          counter: lightbox.querySelector('[data-bi-counter]'),
          media: lightbox.querySelector('[data-bi-media]'),
          thumbs: lightbox.querySelector('[data-bi-thumbs]'),
          name: lightbox.querySelector('[data-bi-name]'),
          common: lightbox.querySelector('[data-bi-common]'),
          desc: lightbox.querySelector('[data-bi-desc]'),
          facts: lightbox.querySelector('[data-bi-facts]'),
          note: lightbox.querySelector('[data-bi-note]'),
          close: lightbox.querySelector('[data-bi-close]')
        }
      : null;

    function loadDetails() {
      if (details) return Promise.resolve(details);
      if (detailsPromise) return detailsPromise;
      if (!DETAILS_URL) return Promise.resolve({});

      detailsPromise = fetch(DETAILS_URL, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (payload) {
          details = payload;
          return details;
        })
        .catch(function () {
          detailsPromise = null;
          return {};
        });

      return detailsPromise;
    }

    function preload(index) {
      if (index < 0 || index >= gallery.length) return;
      var img = new Image();
      img.src = gallery[index];
    }

    /**
     * Keep the active thumbnail in view as the visitor pages through a gallery.
     * Scrolls the strip directly instead of using scrollIntoView, which would
     * also scroll the page behind the dialog.
     */
    function centerThumb(thumb) {
      if (!thumb || !ui.thumbs) return;
      var strip = ui.thumbs;
      if (strip.scrollWidth <= strip.clientWidth) return;

      // Measured from rects rather than offsetLeft: the strip is not a
      // positioned ancestor, so offsetLeft would be relative to the media
      // column and skewed by the strip's own inset.
      var stripBox = strip.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      var delta =
        thumbBox.left - stripBox.left - (strip.clientWidth - thumbBox.width) / 2;

      var target = strip.scrollLeft + delta;
      target = Math.max(0, Math.min(target, strip.scrollWidth - strip.clientWidth));

      var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (strip.scrollTo) {
        strip.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
      } else {
        strip.scrollLeft = target;
      }
    }

    function show(index) {
      if (!ui || !gallery.length) return;
      position = (index + gallery.length) % gallery.length;

      ui.image.classList.remove('is-ready');
      if (ui.spinner) ui.spinner.hidden = false;
      ui.image.src = gallery[position];

      if (ui.counter) {
        ui.counter.textContent = gallery.length > 1
          ? position + 1 + ' / ' + gallery.length
          : '';
      }

      if (ui.thumbs) {
        Array.prototype.forEach.call(ui.thumbs.children, function (thumb, i) {
          thumb.classList.toggle('is-active', i === position);
        });
        centerThumb(ui.thumbs.children[position]);
      }

      var many = gallery.length > 1;
      if (ui.prev) ui.prev.hidden = !many;
      if (ui.next) ui.next.hidden = !many;

      preload(position + 1);
      preload(position - 1);
    }

    if (ui && ui.image) {
      ui.image.addEventListener('load', function () {
        ui.image.classList.add('is-ready');
        if (ui.spinner) ui.spinner.hidden = true;
      });
      ui.image.addEventListener('error', function () {
        if (ui.spinner) ui.spinner.hidden = true;
      });
    }

    function open(tile) {
      if (!lightbox || !ui) return;
      var id = tile.dataset.biId;

      lastFocused = document.activeElement;

      loadDetails().then(function (data) {
        var record = (data && data[id]) || {};
        var name = record.n || (tile.querySelector('.bi-tile__name') || {}).textContent || '';

        ui.name.textContent = name;
        ui.common.textContent = record.c || '';
        ui.desc.textContent = record.d || '';
        ui.note.textContent = record.d
          ? ''
          : 'A written description for this cultivar is on its way.';

        ui.facts.innerHTML = (record.f || [])
          .map(function (pair) {
            return (
              '<div class="bi-fact"><dt>' + escapeHtml(pair[0]) + '</dt>' +
              '<dd>' + escapeHtml(pair[1]) + '</dd></div>'
            );
          })
          .join('');

        gallery = (record.p || []).slice();
        if (!gallery.length) {
          var fallback = tile.querySelector('.bi-tile__img');
          if (fallback) gallery = [fallback.currentSrc || fallback.src];
        }

        // A handful of cultivars have no photograph at all. Drop the media column
        // rather than opening onto an empty stage with a stale photo counter.
        var hasPhotos = gallery.length > 0;
        lightbox.classList.toggle('bi-lb--no-photo', !hasPhotos);
        if (ui.media) ui.media.hidden = !hasPhotos;

        if (ui.thumbs) {
          ui.thumbs.innerHTML = gallery.length > 1
            ? gallery
                .map(function (url, index) {
                  return (
                    '<button class="bi-lb__thumb" type="button" data-bi-go="' + index +
                    '" aria-label="Photo ' + (index + 1) + '">' +
                    '<img src="' + escapeHtml(url) + '" loading="lazy" alt=""></button>'
                  );
                })
                .join('')
            : '';
        }

        // Reveal before showing the first photo: centering the active thumbnail
        // needs real measurements, which a hidden element cannot provide.
        lightbox.hidden = false;
        document.body.classList.add('bi-lb-open');

        show(0);
        if (ui.close) ui.close.focus();
      });
    }

    function close() {
      if (!lightbox) return;
      lightbox.hidden = true;
      document.body.classList.remove('bi-lb-open');
      if (ui && ui.image) ui.image.removeAttribute('src');
      gallery = [];
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    grid.addEventListener('click', function (event) {
      var trigger = event.target.closest('.bi-tile__open');
      if (!trigger) return;
      var tile = trigger.closest('.bi-tile');
      if (tile) open(tile);
    });

    if (lightbox) {
      lightbox.addEventListener('click', function (event) {
        if (event.target.closest('[data-bi-close]')) return close();
        if (event.target.closest('[data-bi-prev]')) return show(position - 1);
        if (event.target.closest('[data-bi-next]')) return show(position + 1);
        var thumb = event.target.closest('[data-bi-go]');
        if (thumb) show(parseInt(thumb.dataset.biGo, 10));
      });

      document.addEventListener('keydown', function (event) {
        if (lightbox.hidden) return;
        if (event.key === 'Escape') {
          close();
        } else if (event.key === 'ArrowLeft') {
          show(position - 1);
        } else if (event.key === 'ArrowRight') {
          show(position + 1);
        } else if (event.key === 'Tab') {
          // Keep focus inside the dialog.
          var focusable = lightbox.querySelectorAll(
            'button:not([hidden]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (!focusable.length) return;
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    }

    // ---- go --------------------------------------------------------------
    if (controls) controls.hidden = false;
    apply();
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-bi-root]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
